import { useState } from "react";
import { Card } from "../../components/ui/Card";
import {
  Bug,
  Send,
  Loader2,
  Copy,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  Calendar,
  Hash,
  Layers,
  BookOpen,
  Shuffle,
  ArrowUpDown,
  Trophy,
  MessageSquare,
  Eye,
} from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import { createApiClient } from "../../lib/api/client";
import { StreamingMarkdownRenderer } from "../../components/chat/StreamingMarkdownRenderer";

interface DocumentResult {
  title: string;
  content: string;
  formatted_content?: {
    ops: Array<{
      insert: string;
      delete?: any;
      retain?: any;
      attributes?: Record<string, any>;
    }>;
  };
  meta?: any;
  id: number;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  deleted_at?: string | null;
  score: number;
  references: any[];
}

interface ChunkResult {
  source_document_id: string;
  content: string;
  score: number;
  metadata: {
    title: string;
    source: string;
    chunk_id: string;
    parent_id: string;
    chunk_size: number;
    created_at: string;
    updated_at: string;
    chunk_index: number;
    total_chunks: number;
    chunk_overlap: number;
    document_title: string;
    indexing_timestamp: string;
    [key: string]: any;
  };
}

interface SearchApiResponse {
  results: (DocumentResult | ChunkResult)[];
  total: number;
  page: number;
  size: number;
  from_: number;
  to: number;
  has_next: boolean;
  has_prev: boolean;
  search_id?: string;
}

interface RerankDocumentInput {
  id: string;
  content: string;
  score: number;
  metadata: Record<string, any>;
}

interface RerankRequest {
  query: string;
  documents: RerankDocumentInput[];
  top_n: number;
}

interface RerankDocumentOutput {
  id: string;
  content: string;
  original_score: number;
  rerank_score: number;
  metadata: Record<string, any>;
}

interface RerankResponse {
  reranked_documents: RerankDocumentOutput[];
  execution_time_ms: number;
  reranker_model?: string;
}

interface PipelineStep {
  name: string;
  description: string;
  duration_ms: number;
  input_count: number;
  output_count: number;
  metadata: Record<string, any>;
  error?: string;
}

interface PipelineDebugInfo {
  steps: PipelineStep[];
  total_execution_ms: number;
  pipeline_config: {
    max_initial_chunks: number;
    score_threshold: number;
    top_k_parents: number;
    use_reranking: boolean;
    reranker_available: boolean;
  };
  pipeline_error?: string;
}

async function handleStreamResponse(
  response: Response,
  onChunk: (chunk: string) => void,
  onComplete: () => void
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Failed to get response reader");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();

    if (done) {
      onComplete();
      break;
    }

    const chunk = decoder.decode(value, { stream: true });
    buffer += chunk;

    // Process complete lines - SSE uses \n\n as separator
    const events = buffer.split("\n\n");
    buffer = events.pop() || ""; // Keep the last incomplete event in buffer

    for (const event of events) {
      const lines = event.split("\n");

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = line.slice(6); // Don't trim here to preserve spacing
          if (data && data !== "[DONE]") {
            try {
              // Try to parse as JSON (chat endpoint format)
              const parsed = JSON.parse(data);
              if (parsed.chunk !== undefined) {
                onChunk(parsed.chunk);
              }
              if (parsed.done) {
                onComplete();
                return;
              }
            } catch (e) {
              // If it's not JSON, treat it as plain text (RAG endpoint format)
              if (!data.startsWith("__SOURCES__")) {
                onChunk(data);
              }
            }
          }
        }
      }
    }
  }
}

function useDebugApi() {
  const { getToken } = useAuth();
  const api = createApiClient(() => getToken());

  return {
    async testCompleteRAGPipeline(
      query: string,
      maxChunks: number = 30,
      includeDebug: boolean = true
    ): Promise<any> {
      const response = await api.post<any>("/rag/complete", {
        query,
        max_chunks: maxChunks,
        context_limit: 5,
        use_reranking: true,
        format_output: true,
        max_sources: 5,
        include_debug: includeDebug,
        format_type: "immediate",
      });
      return response.data;
    },

    async testSearch(
      query: string,
      semantic: boolean = true,
      page: number = 0,
      size: number = 10,
      limit: number = 10,
      returnChunks: boolean = false
    ): Promise<SearchApiResponse> {
      const response = await api.get<SearchApiResponse>("/search", {
        params: {
          q: query,
          page,
          size,
          semantic,
          limit,
          return_chunks: returnChunks,
        },
      });
      return response.data;
    },

    async rerankDocuments(request: RerankRequest): Promise<RerankResponse> {
      const response = await api.post<RerankResponse>(
        "/search/rerank",
        request
      );
      return response.data;
    },

    async fetchFullDocuments(documentIds: number[]): Promise<DocumentResult[]> {
      // Fetch documents by their IDs using the documents endpoint
      console.log("Debug: fetchFullDocuments called with IDs:", documentIds);

      const documents = await Promise.all(
        documentIds.map(async (id) => {
          try {
            console.log(`Debug: Fetching document ${id} from /documents/${id}`);
            const response = await api.get<DocumentResult>(`/documents/${id}`);
            console.log(`Debug: Document ${id} HTTP status:`, response.status);
            console.log(`Debug: Document ${id} full response:`, response);
            console.log(`Debug: Document ${id} response data:`, {
              id: response.data.id,
              title: response.data.title,
              contentLength: response.data.content?.length || 0,
              contentPreview:
                response.data.content?.slice(0, 100) + "..." || "No content",
              hasContent: !!response.data.content,
              rawContentType: typeof response.data.content,
              // Check if this looks like a full document or just a chunk
              isLikelyFullDocument: (response.data.content?.length || 0) > 2000,
              // Log some metadata to verify we got the right document
              createdAt: response.data.created_at,
              updatedAt: response.data.updated_at,
              // Log the complete response structure to see what fields are available
              responseKeys: Object.keys(response.data),
            });

            // Also log the raw content to inspect it
            console.log(
              `Debug: Document ${id} content sample (first 500 chars):`,
              response.data.content?.slice(0, 500)
            );

            // Check if this might be chunk content by looking for chunk-like patterns
            const content = response.data.content || "";
            const hasChunkPattern =
              content.includes("chunk") || content.length < 1000;
            if (hasChunkPattern) {
              console.warn(
                `⚠️ Document ${id} might be returning chunk content instead of full document!`
              );
            }

            // ADDITIONAL TEST: Try the /content endpoint to compare
            try {
              console.log(
                `Debug: Also trying /documents/${id}/content endpoint for comparison`
              );
              const contentResponse = await api.get(`/documents/${id}/content`);
              console.log(`Debug: Content endpoint response:`, {
                contentLength: contentResponse.data.content?.length || 0,
                titleMatches:
                  contentResponse.data.title === response.data.title,
                contentMatches:
                  contentResponse.data.content === response.data.content,
                contentEndpointKeys: Object.keys(contentResponse.data),
              });

              if (contentResponse.data.content !== response.data.content) {
                console.warn(
                  `⚠️ MISMATCH: Content from /documents/${id} vs /documents/${id}/content differs!`
                );
                console.log(
                  "Main endpoint content length:",
                  response.data.content?.length
                );
                console.log(
                  "Content endpoint content length:",
                  contentResponse.data.content?.length
                );
              }
            } catch (contentError) {
              console.warn(
                `Debug: Could not fetch /documents/${id}/content:`,
                contentError
              );
            }

            return response.data;
          } catch (error: any) {
            console.error(`Failed to fetch document ${id}:`, error);
            if (error.response) {
              console.error(`Error response status:`, error.response.status);
              console.error(`Error response data:`, error.response.data);
            }
            return null;
          }
        })
      );

      const validDocs = documents.filter(
        (doc) => doc !== null
      ) as DocumentResult[];
      console.log(
        "Debug: fetchFullDocuments returning:",
        validDocs.length,
        "documents"
      );
      return validDocs;
    },
  };
}

function isChunkResult(
  result: DocumentResult | ChunkResult
): result is ChunkResult {
  return "source_document_id" in result;
}

export function DebugPage(): JSX.Element {
  const { getToken } = useAuth();
  const debugApi = useDebugApi();

  // Search state
  const [query, setQuery] = useState<string>("");
  const [limit, setLimit] = useState<number>(20); // Configurable limit for chunk fetching
  const [searchResults, setSearchResults] = useState<SearchApiResponse | null>(
    null
  );
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [expandedResults, setExpandedResults] = useState<Set<string>>(
    new Set()
  );
  const [isResultsSectionCollapsed, setIsResultsSectionCollapsed] =
    useState<boolean>(false);

  // Reranking state
  const [rerankResults, setRerankResults] = useState<RerankResponse | null>(
    null
  );
  const [isReranking, setIsReranking] = useState<boolean>(false);
  const [rerankError, setRerankError] = useState<string | null>(null);
  const [rerankTopN, setRerankTopN] = useState<number>(10);
  const [isRerankSectionCollapsed, setIsRerankSectionCollapsed] =
    useState<boolean>(false);
  const [expandedRerankResults, setExpandedRerankResults] = useState<
    Set<string>
  >(new Set());
  const [showAllResults, setShowAllResults] = useState<boolean>(false);

  // Context building state
  const [systemPrompt, setSystemPrompt] = useState<string>(
    `
        Vous êtes un assistant juridique expert en droit du travail au Québec.

        Votre objectif est de fournir des réponses **exclusivement à partir du contexte fourni**.

        Règles absolues :
        - Vous devez répondre uniquement en reproduisant intégralement le contenu pertinent du contexte.  
        - Vous ne devez **jamais synthétiser, résumer, reformuler, paraphraser ou adapter** les extraits.  
        - La réponse finale doit être une **reproduction mot pour mot** des formulations juridiques, articles de loi, extraits de décisions, ou explications incluses dans le contexte.  

        Précisions :
        - Si un article de loi est mentionné, vous devez le **citer intégralement tel qu’il apparaît**.  
        - Si une jurisprudence ou une décision est incluse, vous devez reproduire le texte intégral fourni, y compris le raisonnement et la référence (tribunal, juge, numéro), sans modification.  
        - Si une explication juridique ou factuelle est donnée, vous devez la reproduire intégralement, sans la simplifier ni la transformer.  
        - Si la question demande « quel est l’article », vous devez inclure l’article complet présent dans le contexte.  
        - Si la question demande une définition, une explication ou des conditions, vous devez reproduire intégralement le passage du contexte correspondant.  

        Insuffisance d’information :
        - Si le contexte ne contient pas assez d’éléments pour répondre, vous devez écrire uniquement :  
        « Je ne dispose pas de suffisamment d'informations dans les documents fournis pour répondre à cette question avec précision. »

        Interdictions :
        - Ne jamais inventer, compléter ou interpréter.  
        - Ne jamais produire de contenu extérieur au contexte.  
        - Ne pas rédiger de documents formels (politiques, contrats).  

        Style attendu :
        - Ton professionnel et clair, adapté à un gestionnaire RH.  
        - Réponse dans la langue de la question (par défaut : français).  
        - Paragraphes séparés par doubles sauts de ligne.  
        - Listes formatées avec des tirets suivis d’un saut de ligne.  
        - Ne pas reformuler la question.  

        Historique du dialogue (question + contexte fourni) précède toujours votre réponse.  
        Vous devez répondre uniquement à la question posée en reproduisant textuellement le contenu du contexte.
        ---

        Contexte :  
        {context}

        Historique du dialogue :  
        {chat_history}

        Question :  
        {query}

        Réponse :
    `
  );
  const [formattedPrompt, setFormattedPrompt] = useState<string>("");
  const [isContextSectionCollapsed, setIsContextSectionCollapsed] =
    useState<boolean>(false);
  const [chatHistory, setChatHistory] = useState<string>(
    "No previous messages"
  );
  const [useFullDocuments, setUseFullDocuments] = useState<boolean>(false);
  const [maxFullDocuments, setMaxFullDocuments] = useState<number>(5);
  const [fullDocumentsData, setFullDocumentsData] = useState<
    DocumentResult[] | null
  >(null);

  // Complete RAG Pipeline state (new unified approach)
  const [completeRAGResults, setCompleteRAGResults] = useState<any | null>(
    null
  );
  const [isCompleteRAGRunning, setIsCompleteRAGRunning] =
    useState<boolean>(false);
  const [completeRAGError, setCompleteRAGError] = useState<string | null>(null);
  const [isCompleteRAGSectionCollapsed, setIsCompleteRAGSectionCollapsed] =
    useState<boolean>(false);
  const [expandedCompleteRAGResults, setExpandedCompleteRAGResults] = useState<
    Set<string>
  >(new Set());
  const [showAllRerankResults, setShowAllRerankResults] =
    useState<boolean>(false);

  const handleSearch = async (): Promise<void> => {
    if (!query.trim()) return;

    setIsSearching(true);
    setSearchError(null);

    try {
      console.log("Debug: Searching with params:", {
        query,
        semantic: true,
        page: 0,
        size: limit,
        limit,
        returnChunks: true,
      });
      const results = await debugApi.testSearch(
        query,
        true,
        0,
        limit,
        limit,
        true
      ); // Use configurable limit for both size and limit
      console.log("Debug: Search results:", results);
      setSearchResults(results);
      setExpandedResults(new Set()); // Reset expanded state
      setShowAllResults(false); // Reset to show only top 5 results

      // If full documents option is enabled, try to fetch full documents from search results
      if (useFullDocuments && results.results.length > 0) {
        await handleFetchFullDocumentsFromSearch(results.results);
      }
    } catch (error: any) {
      console.error("Search failed:", error);
      setSearchError(
        error.response?.data?.detail || error.message || "Search failed"
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleRerank = async (): Promise<void> => {
    if (!searchResults || !query.trim()) return;

    setIsReranking(true);
    setRerankError(null);

    try {
      // Convert search results to rerank request format
      const documents: RerankDocumentInput[] = searchResults.results.map(
        (result, index) => {
          if (isChunkResult(result)) {
            return {
              id: result.metadata.chunk_id,
              content: result.content,
              score: result.score,
              metadata: result.metadata,
            };
          } else {
            return {
              id: result.id.toString(),
              content: result.content,
              score: result.score,
              metadata: {
                title: result.title,
                id: result.id,
                created_at: result.created_at,
                updated_at: result.updated_at,
              },
            };
          }
        }
      );

      const rerankRequest: RerankRequest = {
        query: query,
        documents: documents,
        top_n: Math.min(rerankTopN, documents.length),
      };

      console.log("Debug: Reranking with request:", rerankRequest);
      const results = await debugApi.rerankDocuments(rerankRequest);
      console.log("Debug: Rerank results:", results);
      setRerankResults(results);
      setExpandedRerankResults(new Set()); // Reset expanded state

      // If full documents option is enabled, fetch full documents for top chunks
      if (useFullDocuments && results.reranked_documents.length > 0) {
        await handleFetchFullDocuments(results.reranked_documents);
      }
    } catch (error: any) {
      console.error("Reranking failed:", error);
      setRerankError(
        error.response?.data?.detail || error.message || "Reranking failed"
      );
    } finally {
      setIsReranking(false);
    }
  };

  const handleFetchFullDocumentsFromSearch = async (
    searchResults: (DocumentResult | ChunkResult)[]
  ): Promise<void> => {
    try {
      // Extract unique source document IDs from search results (chunks)
      const uniqueSourceIds = new Set<string>();

      console.log(
        "Debug: Extracting source IDs from search results:",
        searchResults
      );

      // Debug: Log the first search result structure
      if (searchResults.length > 0) {
        console.log("Debug: First search result structure:", {
          type: isChunkResult(searchResults[0])
            ? "ChunkResult"
            : "DocumentResult",
          result: searchResults[0],
          hasSourceDocumentId: isChunkResult(searchResults[0])
            ? !!searchResults[0].source_document_id
            : "N/A",
          sourceDocumentIdValue: isChunkResult(searchResults[0])
            ? searchResults[0].source_document_id
            : "N/A",
          metadataSource:
            (searchResults[0] as any).metadata?.source || "Not found",
          metadataParentId:
            (searchResults[0] as any).metadata?.parent_id || "Not found",
        });
      }

      for (const result of searchResults.slice(0, maxFullDocuments)) {
        console.log("Debug: Processing search result:", result);

        let sourceId = null;

        if (isChunkResult(result)) {
          // For chunk results, the source_document_id field at root level contains the document ID
          console.log("Debug: Processing chunk result:", {
            chunkId: result.metadata?.chunk_id || "Unknown",
            sourceDocumentId: result.source_document_id || "Missing",
            metadataSource: result.metadata?.source || "Missing",
            metadataParentId: result.metadata?.parent_id || "Missing",
            chunkContentLength: result.content?.length || 0,
          });

          if (result.source_document_id) {
            sourceId = result.source_document_id;
            console.log("Debug: ✓ Found source_document_id:", sourceId);
          }
          // Fallback to metadata fields
          else if (result.metadata?.source) {
            sourceId = result.metadata.source;
            console.log("Debug: ✓ Found metadata.source:", sourceId);
          } else if (result.metadata?.parent_id) {
            sourceId = result.metadata.parent_id;
            console.log("Debug: ✓ Found metadata.parent_id:", sourceId);
          } else {
            console.error("Debug: ❌ No source document ID found in chunk!");
          }
        } else {
          // For document results, use the document ID directly
          sourceId = result.id.toString();
          console.log("Debug: Using document ID directly:", sourceId);
        }

        if (sourceId) {
          uniqueSourceIds.add(String(sourceId));
          console.log("Debug: Found source ID from search:", sourceId);
        } else {
          console.warn("Debug: No source ID found for search result:", result);
        }
      }

      console.log(
        "Debug: Unique source IDs from search:",
        Array.from(uniqueSourceIds)
      );

      if (uniqueSourceIds.size === 0) {
        console.warn("No source document IDs found in search results");
        return;
      }

      // Convert string IDs to numbers for the API call
      const documentIds = Array.from(uniqueSourceIds)
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));

      if (documentIds.length === 0) {
        console.warn("No valid document IDs found in search results");
        return;
      }

      console.log(
        "Debug: Fetching full documents from search for IDs:",
        documentIds
      );
      const fullDocs = await debugApi.fetchFullDocuments(documentIds);
      console.log("Debug: Fetched full documents from search:", fullDocs);

      // Verify we got full documents, not chunks
      if (fullDocs.length > 0 && searchResults.length > 0) {
        const fullDoc = fullDocs[0];
        const chunk = searchResults[0];

        console.log("Debug: Verification - Content comparison:");
        console.log("  Full document ID:", fullDoc.id);
        console.log("  Full document title:", fullDoc.title);
        console.log("  Full document content length:", fullDoc.content.length);
        console.log(
          "  Full document content preview:",
          fullDoc.content.slice(0, 200) + "..."
        );

        console.log(
          "  Original chunk content length:",
          chunk?.content?.length || "N/A"
        );
        console.log(
          "  Original chunk content preview:",
          chunk?.content?.slice(0, 200) + "..." || "N/A"
        );

        const isLonger = fullDoc.content.length > (chunk?.content?.length || 0);
        console.log("  Full document is longer than chunk:", isLonger);

        if (!isLonger) {
          console.warn(
            "⚠️ WARNING: Full document is not longer than chunk! This suggests we might not be getting the full document."
          );
        } else {
          console.log(
            "✅ SUCCESS: Full document is longer than chunk, indicating we got the complete document."
          );
        }
      }

      setFullDocumentsData(fullDocs);
    } catch (error: any) {
      console.error("Failed to fetch full documents from search:", error);
      setSearchError(
        `Failed to fetch full documents: ${error.message || "Unknown error"}`
      );
    }
  };

  const handleFetchFullDocuments = async (
    rerankDocs: RerankDocumentOutput[]
  ): Promise<void> => {
    try {
      // Extract unique source document IDs from chunks
      const uniqueSourceIds = new Set<string>();

      console.log(
        "Debug: Extracting source IDs from reranked docs:",
        rerankDocs
      );

      for (const doc of rerankDocs.slice(0, maxFullDocuments)) {
        console.log("Debug: Processing reranked doc:", doc);

        // Try multiple potential metadata fields for source document ID
        let sourceId = null;

        // Check for source_document_id in metadata (this is the primary field)
        if (doc.metadata?.source_document_id) {
          sourceId = doc.metadata.source_document_id;
          console.log("Debug: Found metadata.source_document_id:", sourceId);
        }
        // Check for source in metadata
        else if (doc.metadata?.source) {
          sourceId = doc.metadata.source;
          console.log("Debug: Found metadata.source:", sourceId);
        }
        // Check for parent_id in metadata
        else if (doc.metadata?.parent_id) {
          sourceId = doc.metadata.parent_id;
          console.log("Debug: Found metadata.parent_id:", sourceId);
        }
        // Check if the ID itself is the source document ID
        else if (doc.id && doc.id.includes("-")) {
          // For chunk IDs like "chunk-123-1", extract "123"
          const parts = doc.id.split("-");
          if (parts.length >= 2) {
            sourceId = parts[1]; // Assumes format like "chunk-{docId}-{chunkIndex}"
            console.log("Debug: Extracted from chunk ID:", sourceId);
          }
        }

        if (sourceId) {
          uniqueSourceIds.add(String(sourceId));
          console.log("Debug: Found source ID:", sourceId);
        } else {
          console.warn("Debug: No source ID found for doc:", doc);
        }
      }

      console.log(
        "Debug: Unique source IDs found:",
        Array.from(uniqueSourceIds)
      );

      if (uniqueSourceIds.size === 0) {
        console.warn("No source document IDs found in reranked results");
        setRerankError("Could not extract source document IDs from chunks");
        return;
      }

      // Convert string IDs to numbers for the API call
      const documentIds = Array.from(uniqueSourceIds)
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));

      if (documentIds.length === 0) {
        console.warn("No valid document IDs found");
        setRerankError("Could not parse document IDs as numbers");
        return;
      }

      console.log("Debug: Fetching full documents for IDs:", documentIds);
      const fullDocs = await debugApi.fetchFullDocuments(documentIds);
      console.log("Debug: Fetched full documents:", fullDocs);
      setFullDocumentsData(fullDocs);
    } catch (error: any) {
      console.error("Failed to fetch full documents:", error);
      setRerankError(
        `Failed to fetch full documents: ${error.message || "Unknown error"}`
      );
    }
  };

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const toggleResultExpansion = (resultId: string): void => {
    setExpandedResults((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  const toggleRerankResultExpansion = (resultId: string): void => {
    setExpandedRerankResults((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  const toggleCompleteRAGResultExpansion = (resultId: string): void => {
    setExpandedCompleteRAGResults((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const getResultId = (result: DocumentResult | ChunkResult): string => {
    return isChunkResult(result)
      ? result.metadata.chunk_id
      : result.id.toString();
  };

  const getResultTitle = (result: DocumentResult | ChunkResult): string => {
    return isChunkResult(result)
      ? result.metadata.document_title
      : result.title;
  };

  const findOriginalPosition = (rerankDoc: RerankDocumentOutput): number => {
    if (!searchResults) return -1;
    const originalIndex = searchResults.results.findIndex((result) => {
      const resultId = isChunkResult(result)
        ? result.metadata.chunk_id
        : result.id.toString();
      return resultId === rerankDoc.id;
    });
    return originalIndex + 1; // Convert to 1-based indexing
  };

  const calculatePositionChange = (
    rerankDoc: RerankDocumentOutput,
    newPosition: number
  ): number => {
    const originalPosition = findOriginalPosition(rerankDoc);
    if (originalPosition === -1) return 0;
    return originalPosition - newPosition;
  };

  const buildContext = (useReranked: boolean = false): string => {
    // If full documents are available and the option is enabled, use them
    if (useFullDocuments && fullDocumentsData && fullDocumentsData.length > 0) {
      const contextParts = fullDocumentsData
        .slice(0, maxFullDocuments)
        .map((doc, index) => {
          return `[Full Document ${index + 1}: ${doc.title}]\n${doc.content}`;
        });
      return contextParts.join("\n\n---\n\n");
    }

    // Otherwise, use the chunk-based approach
    const resultsToUse =
      useReranked && rerankResults
        ? rerankResults.reranked_documents.map((doc) => ({
            content: doc.content,
            metadata: doc.metadata,
            score: doc.rerank_score,
          }))
        : searchResults?.results || [];

    if (resultsToUse.length === 0) {
      return "No search results available to build context.";
    }

    const contextParts = resultsToUse.slice(0, 5).map((result, index) => {
      if ("content" in result) {
        const title =
          "metadata" in result && result.metadata?.document_title
            ? result.metadata.document_title
            : `Document ${index + 1}`;
        return `[Document ${index + 1}: ${title}]\n${result.content}`;
      }
      return `[Document ${index + 1}]\n${(result as any).content}`;
    });

    return contextParts.join("\n\n---\n\n");
  };

  // Helper function to find ranking information for sources
  const getSourceRankingInfo = (sourceId: string) => {
    if (!completeRAGResults?.pipeline_debug?.steps) return null;

    const rerankStep = completeRAGResults.pipeline_debug.steps.find(
      (step: any) => step.name === "reranking"
    );

    if (!rerankStep?.metadata?.reranked_results) return null;

    const rerankDoc = rerankStep.metadata.reranked_results.find(
      (doc: any) => doc.id === sourceId
    );

    if (!rerankDoc) return null;

    const finalPosition =
      completeRAGResults.sources.findIndex(
        (source: any) => source.id === sourceId
      ) + 1;

    return {
      originalPosition: rerankDoc.original_position || null,
      rerankPosition: rerankDoc.rerank_position || null,
      finalPosition: finalPosition,
      originalScore: rerankDoc.original_score,
      rerankScore: rerankDoc.rerank_score,
    };
  };

  const handleFormatPrompt = (useReranked: boolean = false): void => {
    if (!query.trim()) {
      setFormattedPrompt("Please enter a search query first.");
      return;
    }

    const context = buildContext(useReranked);

    const formatted = systemPrompt
      .replace("{query}", query)
      .replace("{context}", context)
      .replace("{chat_history}", chatHistory);

    setFormattedPrompt(formatted);
  };

  const handleCompleteRAGPipeline = async (): Promise<void> => {
    if (!query.trim()) return;

    setIsCompleteRAGRunning(true);
    setCompleteRAGError(null);
    setShowAllRerankResults(false); // Reset to show only top 5 for new results
    setCompleteRAGResults(null); // Clear previous results including generated response

    try {
      console.log("Debug: Running complete RAG pipeline with params:", {
        query,
        max_chunks: limit,
      });
      const results = await debugApi.testCompleteRAGPipeline(
        query,
        limit,
        true
      );
      console.log("Debug: Complete RAG pipeline results:", results);
      setCompleteRAGResults(results);
    } catch (error: any) {
      console.error("Complete RAG pipeline failed:", error);
      setCompleteRAGError(
        error.response?.data?.detail ||
          error.message ||
          "Complete RAG pipeline failed"
      );
    } finally {
      setIsCompleteRAGRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Bug className="h-8 w-8 text-purple-600" />
        <h1 className="text-3xl font-bold text-brand-text">
          Search Debug Console
        </h1>
      </div>

      {/* Complete RAG Pipeline Section */}
      <Card>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Send className="h-6 w-6 text-purple-600" />
              <h3 className="text-lg font-semibold text-brand-text">
                Complete RAG Pipeline (Debug Mode)
              </h3>
            </div>
            <button
              onClick={() =>
                setIsCompleteRAGSectionCollapsed(!isCompleteRAGSectionCollapsed)
              }
              className="text-brand-muted hover:text-brand-muted p-1"
            >
              {isCompleteRAGSectionCollapsed ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronUp className="h-5 w-5" />
              )}
            </button>
          </div>

          {!isCompleteRAGSectionCollapsed && (
            <>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Send className="h-5 w-5 text-purple-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-purple-900 mb-1">
                      One-Click RAG Pipeline
                    </h4>
                    <p className="text-sm text-purple-700">
                      Execute the complete RAG workflow: Enhanced Search →
                      Rerank → Full Docs → LLM Generation. This provides
                      detailed debugging information for each step and shows the
                      complete pipeline execution.
                    </p>
                    <div className="mt-2 text-xs text-purple-600 bg-purple-100 rounded px-2 py-1 inline-block">
                      💡 This uses the same services as the Chat page for
                      consistency
                    </div>
                  </div>
                </div>
              </div>

              {/* Query Input */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-2">
                  Search Query
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Enter your search query..."
                    className="flex-1 rounded-md border border-brand-border px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleCompleteRAGPipeline()
                    }
                  />
                </div>
              </div>

              {/* Chunk Limit Parameter */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-brand-text mb-2">
                    Chunk Limit
                  </label>
                  <input
                    type="number"
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value) || 20)}
                    min="1"
                    max="100"
                    className="w-32 rounded-md border border-brand-border px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
                  />
                </div>
                <div className="flex-1 text-xs text-brand-muted mt-6">
                  🧱 This debug console shows chunk results only (semantic
                  search)
                  <br />
                  💡 Adjust limit to test different chunk retrieval volumes
                </div>
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={handleCompleteRAGPipeline}
                  disabled={isCompleteRAGRunning || !query.trim()}
                  className="rounded-md bg-purple-600 px-6 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isCompleteRAGRunning ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  Execute Complete RAG Pipeline
                </button>
                {query.trim() && (
                  <div className="text-sm text-brand-muted">
                    Query: "{query}" | Max Chunks: {limit}
                  </div>
                )}
              </div>

              {/* Pipeline status */}
              {completeRAGResults && !isCompleteRAGRunning && (
                <div className="flex items-center gap-2 text-sm text-brand-muted">
                  <span>
                    💡 Tip: Adjust your query and chunk limit, then execute the
                    pipeline to see different results
                  </span>
                </div>
              )}

              {/* Complete RAG Error */}
              {completeRAGError && (
                <div className="flex items-center gap-2 text-brand-error bg-brand-error/10 border border-brand-error/30 rounded p-3">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">Complete RAG Error:</span>
                  <span>{completeRAGError}</span>
                </div>
              )}

              {/* Complete RAG Results */}
              {completeRAGResults && (
                <div className="space-y-6">
                  {/* Pipeline Overview */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-medium text-purple-900 mb-3">
                      Complete RAG Execution Summary
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Total Time:</span>
                        <div className="text-purple-700">
                          {completeRAGResults.execution_time_ms}ms
                        </div>
                      </div>
                      <div>
                        <span className="font-medium">Sources Found:</span>
                        <div className="text-purple-700">
                          {completeRAGResults.sources?.length || 0} documents
                        </div>
                      </div>
                      <div>
                        <span className="font-medium">Response Length:</span>
                        <div className="text-purple-700">
                          {completeRAGResults.response?.length.toLocaleString() ||
                            0}{" "}
                          chars
                        </div>
                      </div>
                      <div>
                        <span className="font-medium">Session ID:</span>
                        <div className="text-purple-700 font-mono text-xs">
                          {completeRAGResults.session_id?.slice(0, 8)}...
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pipeline Debug Information */}
                  {completeRAGResults.pipeline_debug && (
                    <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-4">
                      <h4 className="font-medium text-brand-primary mb-3">
                        Pipeline Debug Information
                      </h4>
                      <div className="bg-brand-bg rounded p-4 border border-indigo-200 max-h-96 overflow-y-auto">
                        <pre className="whitespace-pre-wrap text-sm text-brand-text font-mono">
                          {JSON.stringify(
                            completeRAGResults.pipeline_debug,
                            null,
                            2
                          )}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Detailed Reranked Results */}
                  {completeRAGResults.pipeline_debug?.steps &&
                    completeRAGResults.pipeline_debug.steps.find(
                      (step: any) => step.name === "reranking"
                    )?.metadata?.reranked_results && (
                      <div className="space-y-4">
                        <h4 className="font-medium text-brand-text">
                          Reranked Results (
                          {(() => {
                            const totalResults =
                              completeRAGResults.pipeline_debug.steps.find(
                                (step: any) => step.name === "reranking"
                              )?.metadata?.reranked_results?.length || 0;
                            const displayedResults = showAllRerankResults
                              ? totalResults
                              : Math.min(5, totalResults);
                            return `${displayedResults} of ${totalResults}`;
                          })()}
                          )
                        </h4>
                        <div className="bg-brand-warning/5 border border-brand-warning/20 rounded-lg p-3">
                          <p className="text-sm text-brand-warning">
                            These are the results after AI-powered reranking.
                            Compare the original vs reranked scores to see how
                            relevance ranking improved the results.
                          </p>
                        </div>
                        <div className="space-y-3">
                          {(showAllRerankResults
                            ? completeRAGResults.pipeline_debug.steps.find(
                                (step: any) => step.name === "reranking"
                              )?.metadata?.reranked_results || []
                            : completeRAGResults.pipeline_debug.steps
                                .find((step: any) => step.name === "reranking")
                                ?.metadata?.reranked_results?.slice(0, 5) || []
                          ).map((rerankDoc: any, index: number) => {
                            const isExpanded = expandedCompleteRAGResults.has(
                              rerankDoc.id
                            );
                            const displayContent = isExpanded
                              ? rerankDoc.content
                              : rerankDoc.content.length > 300
                              ? rerankDoc.content.slice(0, 300) + "..."
                              : rerankDoc.content;
                            const newPosition =
                              rerankDoc.rerank_position || index + 1;
                            const originalPosition =
                              rerankDoc.original_position || index + 1;
                            const positionChange =
                              originalPosition - newPosition;
                            const scoreDiff =
                              rerankDoc.rerank_score - rerankDoc.original_score;

                            return (
                              <div
                                key={rerankDoc.id}
                                className="border rounded-lg p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200"
                              >
                                {/* Header */}
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-brand-warning rounded-full">
                                          {newPosition}
                                        </span>
                                        {positionChange !== 0 && (
                                          <div
                                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                              positionChange > 0
                                                ? "bg-brand-teal/10 text-brand-teal"
                                                : "bg-brand-error/10 text-brand-error"
                                            }`}
                                          >
                                            {positionChange > 0 ? "↑" : "↓"}
                                            {Math.abs(positionChange)}
                                            {originalPosition > 0 && (
                                              <span className="text-brand-muted">
                                                (was #{originalPosition})
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      <h4 className="font-medium text-brand-text">
                                        {rerankDoc.metadata?.document_title ||
                                          rerankDoc.metadata?.title ||
                                          `Document ${rerankDoc.id}`}
                                      </h4>
                                    </div>

                                    <div className="flex items-center gap-4 text-xs text-brand-muted flex-wrap">
                                      <span className="flex items-center gap-1">
                                        <Hash className="h-3 w-3" />
                                        ID: {rerankDoc.id}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <FileText className="h-3 w-3" />
                                        Original:{" "}
                                        {rerankDoc.original_score?.toFixed(3) ||
                                          "N/A"}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Trophy className="h-3 w-3" />
                                        Reranked:{" "}
                                        {rerankDoc.rerank_score?.toFixed(3) ||
                                          "N/A"}
                                      </span>
                                      {scoreDiff !== 0 && (
                                        <span
                                          className={`flex items-center gap-1 ${
                                            scoreDiff > 0
                                              ? "text-brand-teal"
                                              : "text-brand-error"
                                          }`}
                                        >
                                          {scoreDiff > 0 ? "+" : ""}
                                          {scoreDiff?.toFixed(3) || "N/A"}
                                        </span>
                                      )}
                                      {rerankDoc.metadata?.chunk_id && (
                                        <span className="flex items-center gap-1">
                                          <Layers className="h-3 w-3" />
                                          Chunk: {rerankDoc.metadata.chunk_id}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 ml-4">
                                    <button
                                      onClick={() =>
                                        copyToClipboard(rerankDoc.content)
                                      }
                                      className="text-brand-muted hover:text-brand-muted p-1"
                                      title="Copy content"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </button>
                                    {rerankDoc.content.length > 300 && (
                                      <button
                                        onClick={() =>
                                          toggleCompleteRAGResultExpansion(
                                            rerankDoc.id
                                          )
                                        }
                                        className="text-brand-muted hover:text-brand-muted p-1"
                                        title={
                                          isExpanded ? "Collapse" : "Expand"
                                        }
                                      >
                                        {isExpanded ? (
                                          <ChevronUp className="h-4 w-4" />
                                        ) : (
                                          <ChevronDown className="h-4 w-4" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Content */}
                                <div className="text-sm text-brand-muted">
                                  <pre className="whitespace-pre-wrap font-sans">
                                    {displayContent}
                                  </pre>
                                </div>

                                {/* Expanded details */}
                                {isExpanded && (
                                  <div className="mt-4 pt-4 border-t border-orange-200">
                                    <h5 className="text-sm font-medium text-brand-text mb-2">
                                      Reranking Details:
                                    </h5>
                                    <div className="bg-brand-warning/5 rounded p-3 text-xs">
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div>
                                          <span className="font-medium">
                                            Original Score:
                                          </span>
                                          <div className="text-brand-muted">
                                            {rerankDoc.original_score?.toFixed(
                                              6
                                            ) || "N/A"}
                                          </div>
                                        </div>
                                        <div>
                                          <span className="font-medium">
                                            Rerank Score:
                                          </span>
                                          <div className="text-brand-muted">
                                            {rerankDoc.rerank_score?.toFixed(
                                              6
                                            ) || "N/A"}
                                          </div>
                                        </div>
                                        <div>
                                          <span className="font-medium">
                                            Score Change:
                                          </span>
                                          <div
                                            className={`${
                                              scoreDiff > 0
                                                ? "text-brand-teal"
                                                : scoreDiff < 0
                                                ? "text-brand-error"
                                                : "text-brand-muted"
                                            }`}
                                          >
                                            {scoreDiff > 0 ? "+" : ""}
                                            {scoreDiff?.toFixed(6) || "N/A"}
                                          </div>
                                        </div>
                                        <div>
                                          <span className="font-medium">
                                            Position Change:
                                          </span>
                                          <div
                                            className={`${
                                              positionChange > 0
                                                ? "text-brand-teal"
                                                : positionChange < 0
                                                ? "text-brand-error"
                                                : "text-brand-muted"
                                            }`}
                                          >
                                            {positionChange > 0
                                              ? `↑${positionChange}`
                                              : positionChange < 0
                                              ? `↓${Math.abs(positionChange)}`
                                              : "No change"}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Show More/Less Button for Reranked Results */}
                        {completeRAGResults.pipeline_debug.steps.find(
                          (step: any) => step.name === "reranking"
                        )?.metadata?.reranked_results?.length > 5 && (
                          <div className="text-center py-4">
                            {!showAllRerankResults ? (
                              <button
                                onClick={() => setShowAllRerankResults(true)}
                                className="rounded-md bg-brand-warning/10 px-4 py-2 text-sm font-medium text-brand-warning hover:bg-brand-warning/20 flex items-center gap-2 mx-auto"
                              >
                                <ChevronDown className="h-4 w-4" />
                                Show{" "}
                                {(completeRAGResults.pipeline_debug.steps.find(
                                  (step: any) => step.name === "reranking"
                                )?.metadata?.reranked_results?.length || 0) -
                                  5}{" "}
                                more reranked results
                              </button>
                            ) : (
                              <button
                                onClick={() => setShowAllRerankResults(false)}
                                className="rounded-md bg-brand-warning/10 px-4 py-2 text-sm font-medium text-brand-warning hover:bg-brand-warning/20 flex items-center gap-2 mx-auto"
                              >
                                <ChevronUp className="h-4 w-4" />
                                Show only top 5 reranked results
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                  {/* Context Used */}
                  {completeRAGResults.context_used && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-brand-text">
                        Context Used for Generation
                      </h4>
                      <div className="bg-brand-surface border border-brand-border rounded-lg p-4">
                        <div className="bg-brand-bg rounded p-4 border border-brand-border max-h-96 overflow-y-auto">
                          <pre className="whitespace-pre-wrap text-sm text-brand-text font-mono">
                            {completeRAGResults.context_used.slice(0, 2000)}
                            {completeRAGResults.context_used.length > 2000 &&
                              "..."}
                          </pre>
                        </div>
                        <div className="mt-2 text-xs text-brand-muted">
                          Showing first 2000 characters of{" "}
                          {completeRAGResults.context_used.length.toLocaleString()}{" "}
                          total
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Generated Response */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-brand-text">
                      Generated Response
                    </h4>
                    <div className="bg-brand-bg border border-brand-border rounded-lg p-4">
                      <div className="text-white max-h-96 overflow-y-auto">
                        {isCompleteRAGRunning ? (
                          <div className="flex items-center gap-2 text-brand-muted">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Generating response...
                          </div>
                        ) : (
                          <StreamingMarkdownRenderer
                            content={completeRAGResults.response}
                            isStreaming={false}
                            showCursor={false}
                            typewriterSpeed={0}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sources */}
                  {completeRAGResults.sources &&
                    completeRAGResults.sources.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="font-medium text-brand-text">
                          Sources Used ({completeRAGResults.sources.length})
                        </h4>
                        <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-3 mb-4">
                          <p className="text-sm text-brand-primary">
                            These are the final sources used for generation.
                            Rankings show:
                            <span className="font-medium">
                              {" "}
                              Initial → Reranked → Final
                            </span>{" "}
                            positions to visualize how reranking affected the
                            order.
                          </p>
                        </div>
                        <div className="space-y-3">
                          {completeRAGResults.sources.map(
                            (source: any, index: number) => {
                              const rankingInfo = getSourceRankingInfo(
                                source.id
                              );
                              const finalPosition = index + 1;

                              return (
                                <div
                                  key={source.id || index}
                                  className="border rounded-lg p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-purple-600 rounded-full">
                                          {finalPosition}
                                        </span>
                                        {rankingInfo && (
                                          <div className="flex items-center gap-2">
                                            {rankingInfo.originalPosition && (
                                              <div className="bg-brand-surface text-brand-text px-2 py-1 rounded text-xs font-medium">
                                                Initial: #
                                                {rankingInfo.originalPosition}
                                              </div>
                                            )}
                                            <div className="bg-brand-warning/10 text-brand-warning px-2 py-1 rounded text-xs font-medium">
                                              Reranked: #
                                              {rankingInfo.rerankPosition}
                                            </div>
                                            <div className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-medium">
                                              Final: #
                                              {rankingInfo.finalPosition}
                                            </div>
                                            {rankingInfo.originalPosition &&
                                              rankingInfo.originalPosition !==
                                                rankingInfo.finalPosition && (
                                                <div
                                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                                    rankingInfo.originalPosition >
                                                    rankingInfo.finalPosition
                                                      ? "bg-brand-teal/10 text-brand-teal"
                                                      : "bg-brand-error/10 text-brand-error"
                                                  }`}
                                                >
                                                  {rankingInfo.originalPosition >
                                                  rankingInfo.finalPosition
                                                    ? "↑"
                                                    : "↓"}
                                                  {Math.abs(
                                                    rankingInfo.originalPosition -
                                                      rankingInfo.finalPosition
                                                  )}
                                                </div>
                                              )}
                                          </div>
                                        )}
                                        <h5 className="font-medium text-brand-text">
                                          {source.title ||
                                            `Source ${index + 1}`}
                                        </h5>
                                      </div>
                                      <div className="flex items-center gap-4 text-xs text-brand-muted flex-wrap">
                                        <span className="flex items-center gap-1">
                                          <Hash className="h-3 w-3" />
                                          ID: {source.id}
                                        </span>
                                        {source.score && (
                                          <span className="flex items-center gap-1">
                                            <Trophy className="h-3 w-3" />
                                            Final Score:{" "}
                                            {source.score.toFixed(3)}
                                          </span>
                                        )}
                                        {rankingInfo && (
                                          <>
                                            {rankingInfo.originalScore && (
                                              <span className="flex items-center gap-1">
                                                <FileText className="h-3 w-3" />
                                                Original:{" "}
                                                {rankingInfo.originalScore.toFixed(
                                                  3
                                                )}
                                              </span>
                                            )}
                                            <span className="flex items-center gap-1">
                                              <Trophy className="h-3 w-3" />
                                              Reranked:{" "}
                                              {rankingInfo.rerankScore.toFixed(
                                                3
                                              )}
                                            </span>
                                          </>
                                        )}
                                        <span className="flex items-center gap-1">
                                          <FileText className="h-3 w-3" />
                                          Length:{" "}
                                          {source.content_length?.toLocaleString() ||
                                            "N/A"}{" "}
                                          chars
                                        </span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() =>
                                        copyToClipboard(source.content || "")
                                      }
                                      className="text-brand-muted hover:text-brand-muted p-1"
                                      title="Copy content"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    )}
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Pipeline Error */}
      {completeRAGError && (
        <Card>
          <div className="flex items-center gap-2 text-brand-error">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Pipeline Error:</span>
            <span>{completeRAGError}</span>
          </div>
        </Card>
      )}

      {/* Search Results - Hide if Complete RAG was used */}
      {searchResults && !completeRAGResults && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() =>
                  setIsResultsSectionCollapsed(!isResultsSectionCollapsed)
                }
                className="flex items-center gap-2 text-lg font-semibold text-brand-text hover:text-purple-600 transition-colors"
              >
                <h3>
                  Search Results{" "}
                  <span className="text-purple-600">(Chunks)</span>
                </h3>
                {isResultsSectionCollapsed ? (
                  <ChevronDown className="h-5 w-5" />
                ) : (
                  <ChevronUp className="h-5 w-5" />
                )}
              </button>
              <div className="flex items-center gap-4 text-sm text-brand-muted">
                <span>
                  Showing {searchResults.from_ + 1}-
                  {showAllResults
                    ? searchResults.to
                    : Math.min(searchResults.from_ + 5, searchResults.to)}{" "}
                  of {searchResults.total}
                  {!showAllResults &&
                    searchResults.results.length > 5 &&
                    ` (top 5 results)`}
                </span>
                {searchResults.search_id && (
                  <span>ID: {searchResults.search_id}</span>
                )}
              </div>
            </div>

            {/* Results List */}
            <div className="space-y-4">
              {searchResults.results.length > 0 ? (
                <>
                  {(showAllResults
                    ? searchResults.results
                    : searchResults.results.slice(0, 5)
                  ).map((result, index) => {
                    const resultId = getResultId(result);
                    const isExpanded = expandedResults.has(resultId);
                    const isChunk = isChunkResult(result);
                    const displayContent = isExpanded
                      ? result.content
                      : result.content.length > 300
                      ? result.content.slice(0, 300) + "..."
                      : result.content;
                    const resultIndex = searchResults.from_ + index + 1; // Calculate absolute index

                    return (
                      <div
                        key={resultId}
                        className={`border rounded-lg p-4 hover:border-brand-border transition-colors ${
                          isChunk
                            ? "border-brand-primary/20 bg-brand-primary/5"
                            : "border-brand-border"
                        }`}
                      >
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-purple-600 rounded-full">
                                  {resultIndex}
                                </span>
                                {isChunk && (
                                  <Layers className="h-4 w-4 text-brand-primary" />
                                )}
                              </div>
                              <h4 className="font-medium text-brand-text">
                                {getResultTitle(result)}
                              </h4>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-brand-muted flex-wrap">
                              {isChunk ? (
                                <>
                                  <span className="flex items-center gap-1">
                                    <Hash className="h-3 w-3" />
                                    Chunk: {result.metadata.chunk_id}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <BookOpen className="h-3 w-3" />
                                    Doc: {result.source_document_id}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Layers className="h-3 w-3" />
                                    {result.metadata.chunk_index + 1}/
                                    {result.metadata.total_chunks}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    Score: {result.score.toFixed(3)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {formatDate(
                                      result.metadata.indexing_timestamp
                                    )}
                                  </span>
                                  <span>
                                    Size: {result.metadata.chunk_size} chars
                                  </span>
                                  <span>
                                    Overlap: {result.metadata.chunk_overlap}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="flex items-center gap-1">
                                    <Hash className="h-3 w-3" />
                                    ID: {result.id}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    Score: {result.score.toFixed(3)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {formatDate(result.updated_at)}
                                  </span>
                                  {result.references.length > 0 && (
                                    <span>
                                      Refs: {result.references.length}
                                    </span>
                                  )}
                                  {result.is_deleted && (
                                    <span className="text-brand-error">
                                      DELETED
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => copyToClipboard(result.content)}
                              className="text-brand-muted hover:text-brand-muted p-1"
                              title="Copy content"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                            {result.content.length > 300 && (
                              <button
                                onClick={() => toggleResultExpansion(resultId)}
                                className="text-brand-muted hover:text-brand-muted p-1"
                                title={isExpanded ? "Collapse" : "Expand"}
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="text-sm text-brand-muted">
                          <pre className="whitespace-pre-wrap font-sans">
                            {displayContent}
                          </pre>
                        </div>

                        {/* Chunk-specific info when expanded */}
                        {isChunk && isExpanded && (
                          <div className="mt-4 pt-4 border-t border-brand-border">
                            <h5 className="text-sm font-medium text-brand-text mb-2">
                              Chunk Information:
                            </h5>
                            <div className="bg-brand-primary/5 rounded p-3 text-xs">
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div>
                                  <span className="font-medium">Chunk ID:</span>
                                  <div className="text-brand-muted font-mono">
                                    {result.metadata.chunk_id}
                                  </div>
                                </div>
                                <div>
                                  <span className="font-medium">Position:</span>
                                  <div className="text-brand-muted">
                                    {result.metadata.chunk_index + 1} of{" "}
                                    {result.metadata.total_chunks}
                                  </div>
                                </div>
                                <div>
                                  <span className="font-medium">Size:</span>
                                  <div className="text-brand-muted">
                                    {result.metadata.chunk_size} chars
                                  </div>
                                </div>
                                <div>
                                  <span className="font-medium">Overlap:</span>
                                  <div className="text-brand-muted">
                                    {result.metadata.chunk_overlap} chars
                                  </div>
                                </div>
                                <div>
                                  <span className="font-medium">
                                    Parent Doc:
                                  </span>
                                  <div className="text-brand-muted">
                                    {result.metadata.parent_id}
                                  </div>
                                </div>
                                <div>
                                  <span className="font-medium">Indexed:</span>
                                  <div className="text-brand-muted">
                                    {formatDate(
                                      result.metadata.indexing_timestamp
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Document Formatted Content Preview (for regular docs) */}
                        {!isChunk &&
                          "formatted_content" in result &&
                          result.formatted_content &&
                          isExpanded && (
                            <div className="mt-4 pt-4 border-t border-brand-border">
                              <h5 className="text-sm font-medium text-brand-text mb-2">
                                Formatted Content (Quill Delta):
                              </h5>
                              <div className="bg-brand-surface rounded p-3 text-xs">
                                <div className="max-h-40 overflow-y-auto">
                                  <pre className="text-brand-muted">
                                    {JSON.stringify(
                                      result.formatted_content,
                                      null,
                                      2
                                    )}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          )}

                        {/* Full Metadata */}
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-brand-border">
                            <h5 className="text-sm font-medium text-brand-text mb-2">
                              Full Metadata:
                            </h5>
                            <div className="bg-brand-surface rounded p-3 text-xs">
                              <div className="max-h-60 overflow-y-auto">
                                <pre className="text-brand-muted">
                                  {JSON.stringify(
                                    isChunk ? result.metadata : result,
                                    null,
                                    2
                                  )}
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Show more button */}
                  {!showAllResults && searchResults.results.length > 5 && (
                    <div className="text-center py-4">
                      <button
                        onClick={() => setShowAllResults(true)}
                        className="rounded-md bg-brand-surface px-4 py-2 text-sm font-medium text-brand-text hover:bg-brand-border flex items-center gap-2 mx-auto"
                      >
                        <ChevronDown className="h-4 w-4" />
                        Show {searchResults.results.length - 5} more results
                      </button>
                    </div>
                  )}

                  {/* Show less button */}
                  {showAllResults && searchResults.results.length > 5 && (
                    <div className="text-center py-4">
                      <button
                        onClick={() => setShowAllResults(false)}
                        className="rounded-md bg-brand-surface px-4 py-2 text-sm font-medium text-brand-text hover:bg-brand-border flex items-center gap-2 mx-auto"
                      >
                        <ChevronUp className="h-4 w-4" />
                        Show only top 5 results
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-brand-muted">
                  No results found for this query.
                </div>
              )}
            </div>

            {/* Search Summary */}
            <div className="mt-6 p-4 bg-brand-surface rounded-lg">
              <h4 className="font-medium text-brand-text mb-3">Search Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium">Query:</span>
                  <div className="text-brand-muted break-words">"{query}"</div>
                </div>
                <div>
                  <span className="font-medium">Chunk Limit:</span>
                  <div className="text-brand-muted">{limit} chunks requested</div>
                </div>
                <div>
                  <span className="font-medium">Total Results:</span>
                  <div className="text-brand-muted">
                    {searchResults.total} chunks found
                  </div>
                </div>
              </div>
              {searchResults.search_id && (
                <div className="mt-3 pt-3 border-t border-brand-border">
                  <span className="font-medium text-sm">Search ID:</span>
                  <div className="text-xs text-brand-muted font-mono">
                    {searchResults.search_id}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Reranking Section - Hide if Complete RAG was used */}
      {searchResults &&
        searchResults.results.length > 0 &&
        !completeRAGResults && (
          <Card>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shuffle className="h-6 w-6 text-brand-warning" />
                  <h3 className="text-lg font-semibold text-brand-text">
                    Document Reranking
                  </h3>
                </div>
                <button
                  onClick={() =>
                    setIsRerankSectionCollapsed(!isRerankSectionCollapsed)
                  }
                  className="text-brand-muted hover:text-brand-muted p-1"
                >
                  {isRerankSectionCollapsed ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronUp className="h-5 w-5" />
                  )}
                </button>
              </div>

              {!isRerankSectionCollapsed && (
                <>
                  <div className="bg-brand-warning/5 border border-brand-warning/20 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Trophy className="h-5 w-5 text-brand-warning mt-0.5" />
                      <div>
                        <h4 className="font-medium text-brand-warning mb-1">
                          Rerank Search Results
                        </h4>
                        <p className="text-sm text-brand-warning">
                          Apply AI-powered reranking to improve result
                          relevance. This will reorder the current search
                          results based on semantic understanding of your query.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-text mb-2">
                          Top N Results
                        </label>
                        <input
                          type="number"
                          value={rerankTopN}
                          onChange={(e) =>
                            setRerankTopN(parseInt(e.target.value) || 10)
                          }
                          min="1"
                          max={searchResults.results.length}
                          className="w-24 rounded-md border border-brand-border px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-orange-500"
                        />
                      </div>
                      <div className="flex-1">
                        <button
                          onClick={handleRerank}
                          disabled={isReranking}
                          className="rounded-md bg-brand-warning px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isReranking ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" />
                          )}
                          Rerank Results
                        </button>
                      </div>
                      <div className="text-xs text-brand-muted">
                        Available: {searchResults.results.length} documents
                      </div>
                    </div>

                    {/* Full Documents Configuration */}
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <BookOpen className="h-5 w-5 text-purple-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-medium text-purple-900 mb-2">
                            Full Documents Context
                          </h4>
                          <p className="text-sm text-purple-700 mb-3">
                            When enabled, fetches complete documents for the top
                            chunks instead of using chunk content for context.
                            This provides more comprehensive context but may use
                            more tokens.
                          </p>

                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={useFullDocuments}
                                onChange={(e) => {
                                  setUseFullDocuments(e.target.checked);
                                  if (!e.target.checked) {
                                    setFullDocumentsData(null); // Clear data when disabled
                                  }
                                }}
                                className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-sm font-medium text-purple-800">
                                Use Full Documents for Context
                              </span>
                            </label>

                            {useFullDocuments && (
                              <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-purple-700">
                                  Max Documents:
                                </label>
                                <input
                                  type="number"
                                  value={maxFullDocuments}
                                  onChange={(e) =>
                                    setMaxFullDocuments(
                                      parseInt(e.target.value) || 5
                                    )
                                  }
                                  min="1"
                                  max="10"
                                  className="w-16 rounded-md border border-purple-300 px-2 py-1 text-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
                                />
                              </div>
                            )}
                          </div>

                          {useFullDocuments &&
                            searchResults &&
                            searchResults.results.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-purple-200">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() =>
                                      handleFetchFullDocumentsFromSearch(
                                        searchResults.results
                                      )
                                    }
                                    className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition-colors"
                                  >
                                    🔄 Fetch Full Documents Now
                                  </button>
                                  <button
                                    onClick={async () => {
                                      // Test direct document fetch with known ID
                                      const knownId =
                                        searchResults.results[0] &&
                                        isChunkResult(searchResults.results[0])
                                          ? searchResults.results[0]
                                              .source_document_id
                                          : searchResults.results[0]?.id;
                                      if (knownId) {
                                        console.log(
                                          "Debug: Testing direct fetch for document ID:",
                                          knownId
                                        );
                                        try {
                                          const directDoc =
                                            await debugApi.fetchFullDocuments([
                                              parseInt(String(knownId)),
                                            ]);
                                          console.log(
                                            "Debug: Direct fetch result:",
                                            directDoc
                                          );
                                        } catch (error) {
                                          console.error(
                                            "Debug: Direct fetch failed:",
                                            error
                                          );
                                        }
                                      }
                                    }}
                                    className="text-sm bg-brand-primary text-brand-surface px-3 py-1 rounded hover:opacity-90 transition-colors"
                                  >
                                    🔍 Test Direct Fetch
                                  </button>
                                </div>
                                {fullDocumentsData &&
                                  fullDocumentsData.length > 0 && (
                                    <div className="text-sm text-purple-700 mt-2">
                                      ✓ Fetched {fullDocumentsData.length} full
                                      document
                                      {fullDocumentsData.length !== 1
                                        ? "s"
                                        : ""}{" "}
                                      for context
                                    </div>
                                  )}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Rerank Error */}
                  {rerankError && (
                    <div className="flex items-center gap-2 text-brand-error bg-brand-error/10 border border-brand-error/30 rounded p-3">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Reranking Error:</span>
                      <span>{rerankError}</span>
                    </div>
                  )}

                  {/* Rerank Results */}
                  {rerankResults && (
                    <div className="space-y-4">
                      <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-brand-teal mb-2">
                            Reranking Complete
                          </h4>
                          <div className="flex items-center gap-4 text-sm text-brand-teal">
                            <span>⚡ {rerankResults.execution_time_ms}ms</span>
                            {rerankResults.reranker_model && (
                              <span>🤖 {rerankResults.reranker_model}</span>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-brand-teal">
                          Processed {rerankResults.reranked_documents.length}{" "}
                          documents. Compare the new rankings with the original
                          results below.
                        </p>
                      </div>

                      <div className="space-y-3">
                        {rerankResults.reranked_documents.map(
                          (rerankDoc, index) => {
                            const isExpanded = expandedRerankResults.has(
                              rerankDoc.id
                            );
                            const displayContent = isExpanded
                              ? rerankDoc.content
                              : rerankDoc.content.length > 300
                              ? rerankDoc.content.slice(0, 300) + "..."
                              : rerankDoc.content;
                            const newPosition = index + 1;
                            const originalPosition =
                              findOriginalPosition(rerankDoc);
                            const positionChange = calculatePositionChange(
                              rerankDoc,
                              newPosition
                            );
                            const scoreDiff =
                              rerankDoc.rerank_score - rerankDoc.original_score;

                            return (
                              <div
                                key={rerankDoc.id}
                                className="border rounded-lg p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200"
                              >
                                {/* Header */}
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-brand-warning rounded-full">
                                          {newPosition}
                                        </span>
                                        {positionChange !== 0 && (
                                          <div
                                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                              positionChange > 0
                                                ? "bg-brand-teal/10 text-brand-teal"
                                                : "bg-brand-error/10 text-brand-error"
                                            }`}
                                          >
                                            {positionChange > 0 ? "↑" : "↓"}
                                            {Math.abs(positionChange)}
                                            {originalPosition > 0 && (
                                              <span className="text-brand-muted">
                                                (was #{originalPosition})
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      <h4 className="font-medium text-brand-text">
                                        {rerankDoc.metadata.document_title ||
                                          rerankDoc.metadata.title ||
                                          `Document ${rerankDoc.id}`}
                                      </h4>
                                    </div>

                                    <div className="flex items-center gap-4 text-xs text-brand-muted flex-wrap">
                                      <span className="flex items-center gap-1">
                                        <Hash className="h-3 w-3" />
                                        ID: {rerankDoc.id}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <FileText className="h-3 w-3" />
                                        Original:{" "}
                                        {rerankDoc.original_score.toFixed(3)}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Trophy className="h-3 w-3" />
                                        Reranked:{" "}
                                        {rerankDoc.rerank_score.toFixed(3)}
                                      </span>
                                      {scoreDiff !== 0 && (
                                        <span
                                          className={`flex items-center gap-1 ${
                                            scoreDiff > 0
                                              ? "text-brand-teal"
                                              : "text-brand-error"
                                          }`}
                                        >
                                          {scoreDiff > 0 ? "+" : ""}
                                          {scoreDiff.toFixed(3)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 ml-4">
                                    <button
                                      onClick={() =>
                                        copyToClipboard(rerankDoc.content)
                                      }
                                      className="text-brand-muted hover:text-brand-muted p-1"
                                      title="Copy content"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </button>
                                    {rerankDoc.content.length > 300 && (
                                      <button
                                        onClick={() =>
                                          toggleRerankResultExpansion(
                                            rerankDoc.id
                                          )
                                        }
                                        className="text-brand-muted hover:text-brand-muted p-1"
                                        title={
                                          isExpanded ? "Collapse" : "Expand"
                                        }
                                      >
                                        {isExpanded ? (
                                          <ChevronUp className="h-4 w-4" />
                                        ) : (
                                          <ChevronDown className="h-4 w-4" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Content */}
                                <div className="text-sm text-brand-muted">
                                  <pre className="whitespace-pre-wrap font-sans">
                                    {displayContent}
                                  </pre>
                                </div>

                                {/* Expanded metadata */}
                                {isExpanded && (
                                  <div className="mt-4 pt-4 border-t border-orange-200">
                                    <h5 className="text-sm font-medium text-brand-text mb-2">
                                      Reranking Details:
                                    </h5>
                                    <div className="bg-brand-warning/5 rounded p-3 text-xs">
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div>
                                          <span className="font-medium">
                                            Original Score:
                                          </span>
                                          <div className="text-brand-muted">
                                            {rerankDoc.original_score.toFixed(
                                              6
                                            )}
                                          </div>
                                        </div>
                                        <div>
                                          <span className="font-medium">
                                            Rerank Score:
                                          </span>
                                          <div className="text-brand-muted">
                                            {rerankDoc.rerank_score.toFixed(6)}
                                          </div>
                                        </div>
                                        <div>
                                          <span className="font-medium">
                                            Score Change:
                                          </span>
                                          <div
                                            className={`${
                                              scoreDiff > 0
                                                ? "text-brand-teal"
                                                : scoreDiff < 0
                                                ? "text-brand-error"
                                                : "text-brand-muted"
                                            }`}
                                          >
                                            {scoreDiff > 0 ? "+" : ""}
                                            {scoreDiff.toFixed(6)}
                                          </div>
                                        </div>
                                        <div>
                                          <span className="font-medium">
                                            Position Change:
                                          </span>
                                          <div
                                            className={`${
                                              positionChange > 0
                                                ? "text-brand-teal"
                                                : positionChange < 0
                                                ? "text-brand-error"
                                                : "text-brand-muted"
                                            }`}
                                          >
                                            {positionChange > 0
                                              ? `↑${positionChange}`
                                              : positionChange < 0
                                              ? `↓${Math.abs(positionChange)}`
                                              : "No change"}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }
                        )}
                      </div>

                      {/* Reranking Summary */}
                      <div className="mt-6 p-4 bg-brand-warning/5 rounded-lg border border-brand-warning/20">
                        <h4 className="font-medium text-brand-warning mb-3">
                          Reranking Summary
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="font-medium">
                              Documents Reranked:
                            </span>
                            <div className="text-brand-warning">
                              {rerankResults.reranked_documents.length}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Execution Time:</span>
                            <div className="text-brand-warning">
                              {rerankResults.execution_time_ms}ms
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Reranker Model:</span>
                            <div className="text-brand-warning">
                              {rerankResults.reranker_model || "Default"}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Query:</span>
                            <div className="text-brand-warning break-words">
                              "{query}"
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Full Documents Display */}
                  {useFullDocuments &&
                    fullDocumentsData &&
                    fullDocumentsData.length > 0 && (
                      <div className="mt-6 space-y-4">
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <h4 className="font-medium text-purple-900 mb-2">
                            Full Documents Retrieved
                          </h4>
                          <p className="text-sm text-purple-700">
                            These complete documents will be used as context
                            instead of individual chunks.
                          </p>
                        </div>

                        <div className="space-y-3">
                          {fullDocumentsData.map((doc, index) => (
                            <div
                              key={doc.id}
                              className="border rounded-lg p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-purple-600 rounded-full">
                                      {index + 1}
                                    </span>
                                    <BookOpen className="h-4 w-4 text-purple-600" />
                                    <h4 className="font-medium text-brand-text">
                                      {doc.title}
                                    </h4>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-brand-muted flex-wrap">
                                    <span className="flex items-center gap-1">
                                      <Hash className="h-3 w-3" />
                                      ID: {doc.id}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {formatDate(doc.updated_at)}
                                    </span>
                                    <span>
                                      Length:{" "}
                                      {doc.content.length.toLocaleString()}{" "}
                                      chars
                                    </span>
                                    {doc.score && (
                                      <span className="flex items-center gap-1">
                                        <FileText className="h-3 w-3" />
                                        Score: {doc.score.toFixed(3)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => copyToClipboard(doc.content)}
                                  className="text-brand-muted hover:text-brand-muted p-1"
                                  title="Copy content"
                                >
                                  <Copy className="h-4 w-4" />
                                </button>
                              </div>

                              <div className="text-sm text-brand-muted">
                                <div className="bg-brand-bg rounded p-3 border border-purple-200 max-h-96 overflow-y-auto">
                                  <pre className="whitespace-pre-wrap font-sans">
                                    {doc.content}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </>
              )}
            </div>
          </Card>
        )}

      {/* Context Building Section - Hide if Complete RAG was used */}
      {(searchResults || rerankResults) &&
        query.trim() &&
        !completeRAGResults && (
          <Card>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-6 w-6 text-brand-teal" />
                  <h3 className="text-lg font-semibold text-brand-text">
                    Context Building
                  </h3>
                </div>
                <button
                  onClick={() =>
                    setIsContextSectionCollapsed(!isContextSectionCollapsed)
                  }
                  className="text-brand-muted hover:text-brand-muted p-1"
                >
                  {isContextSectionCollapsed ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronUp className="h-5 w-5" />
                  )}
                </button>
              </div>

              {!isContextSectionCollapsed && (
                <>
                  <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <MessageSquare className="h-5 w-5 text-brand-teal mt-0.5" />
                      <div>
                        <h4 className="font-medium text-brand-teal mb-1">
                          System Prompt Builder
                        </h4>
                        <p className="text-sm text-brand-teal">
                          Create a system prompt template and see how it gets
                          formatted with search results as context. Use
                          placeholders: {`{query}`}, {`{context}`},{" "}
                          {`{chat_history}`}
                          {useFullDocuments &&
                            fullDocumentsData &&
                            fullDocumentsData.length > 0 && (
                              <span className="block mt-1 text-purple-600 font-medium">
                                📄 Using {fullDocumentsData.length} full
                                document
                                {fullDocumentsData.length !== 1 ? "s" : ""} for
                                context instead of chunks.
                              </span>
                            )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* System Prompt Input */}
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      System Prompt Template
                    </label>
                    <textarea
                      value={systemPrompt}
                      onChange={(e) => setSystemPrompt(e.target.value)}
                      placeholder="Enter your system prompt template with {query}, {context}, and {chat_history} placeholders..."
                      className="w-full h-48 rounded-md border border-brand-border px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-green-500 font-mono"
                    />
                  </div>

                  {/* Chat History Input */}
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Chat History (Optional)
                    </label>
                    <textarea
                      value={chatHistory}
                      onChange={(e) => setChatHistory(e.target.value)}
                      placeholder="Enter previous chat messages or leave default..."
                      className="w-full h-24 rounded-md border border-brand-border px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-green-500"
                    />
                  </div>

                  {/* Format Buttons */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleFormatPrompt(false)}
                      disabled={!searchResults}
                      className="rounded-md bg-brand-teal px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      Format with Search Results
                    </button>
                    {rerankResults && (
                      <button
                        onClick={() => handleFormatPrompt(true)}
                        className="rounded-md bg-brand-warning px-4 py-2 text-sm font-medium text-white hover:opacity-90 flex items-center gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        Format with Reranked Results
                      </button>
                    )}
                    {formattedPrompt && (
                      <button
                        onClick={() => copyToClipboard(formattedPrompt)}
                        className="rounded-md bg-brand-muted px-4 py-2 text-sm font-medium text-white hover:opacity-90 flex items-center gap-2"
                      >
                        <Copy className="h-4 w-4" />
                        Copy Formatted Prompt
                      </button>
                    )}
                  </div>

                  {/* Formatted Prompt Display */}
                  {formattedPrompt && (
                    <div className="space-y-4">
                      <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4">
                        <h4 className="font-medium text-brand-teal mb-3">
                          Formatted Prompt
                        </h4>
                        <div className="bg-brand-bg rounded p-4 border border-green-200">
                          <pre className="whitespace-pre-wrap text-sm text-brand-text font-mono max-h-96 overflow-y-auto">
                            {formattedPrompt}
                          </pre>
                        </div>
                      </div>

                      {/* Context Stats */}
                      <div className="bg-brand-surface rounded-lg p-4">
                        <h4 className="font-medium text-brand-text mb-3">
                          Prompt Statistics
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="font-medium">
                              Total Characters:
                            </span>
                            <div className="text-brand-muted">
                              {formattedPrompt.length.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">
                              Estimated Tokens:
                            </span>
                            <div className="text-brand-muted">
                              ~
                              {Math.ceil(
                                formattedPrompt.length / 4
                              ).toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">
                              Context Sources:
                            </span>
                            <div className="text-brand-muted">
                              {useFullDocuments && fullDocumentsData
                                ? `${fullDocumentsData.length} full documents 📄`
                                : searchResults
                                ? `${Math.min(
                                    5,
                                    searchResults.results.length
                                  )} chunks 🧱`
                                : "0 documents"}
                            </div>
                            {useFullDocuments &&
                              fullDocumentsData &&
                              fullDocumentsData.length > 0 && (
                                <div className="text-xs text-purple-600 mt-1">
                                  Using complete documents instead of chunks for
                                  context
                                </div>
                              )}
                          </div>
                          <div>
                            <span className="font-medium">Query Length:</span>
                            <div className="text-brand-muted">
                              {query.length} chars
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        )}
    </div>
  );
}
